<script>
	import { createEventDispatcher } from 'svelte';
    import { navigate } from 'svelte-routing';
    import { Tooltip } from 'sveltestrap';

    import { hydrateInmate, getInmateSearchModel, getInmateSurveyModel, getInmateContactModel, getInmateDetailsTooltip,
        getArrivalDateAndTimeText, getArrivalDateAndTimeTooltip, getTemporaryDischargeTooltip, getCircumstanceDateTooltip, 
        ModalTypeEnum } from '../../inmate.js'
    import { getEarlyRiskColourClass, getEarlyRiskColourName } from "../../early-risk.js";
    import { getTriggerDatesTooltip, isDateInTriggerDates } from '../../trigger-date.js';
    import { getSurveyScheduleTooltip, getSurveyToastProperties } from "../../survey.js";
    import { getContactWarningTooltip } from '../../contact.js';
    import { isToday, isDateInPast, getIsoDateStringAsDate } from '../../date-time.js';
    import { createRequestParams, isMatching } from "../../string.js";
    import { sortByProperty } from '../../sort.js';
    import { referenceDataEthnicityList, referenceDataRegimeLevelList, referenceDataCellStatusList, referenceDataEarlyRiskTabList, getReferenceDataItemFromList } from "../../reference-data.js";
    import { vptServer, token, reportDate, inmateCount, filterShowTargetedInmatesOnly, getTooltipDisplayOptions } from '../../stores.js';
    import { config } from '../../config.js';

    import AimToast from '../../component/AimToast.svelte';
    import Dropdown from '../../component/Dropdown.svelte';
    import FilterButton from '../../component/FilterButton.svelte';

    import AddContactModal from '../../modal/AddContactModal.svelte';
    import ContactModal from '../../modal/ContactModal.svelte';
    import SurveyModal from '../../modal/SurveyModal.svelte';

    import TableHead from '../../table/TableHead.svelte';
    import TableData from '../../table/TableData.svelte';
    import TableAction from '../../table/TableAction.svelte';
    import TargetedData from '../../table/TargetedData.svelte';
    import InmateData from '../../table/InmateData.svelte';
    import LocationData from '../../table/LocationData.svelte';

    export let requestProperties;

    const dispatch = createEventDispatcher();

    let inmates = [];
    let filteredInmates = [];
    let sites = [];
    let searchCompleted = false;
    let sortProperty = "rowOrdering";
    let reverseSorted = false;

    let toastProperties = null; 
    
    // Table filters and controls
    let filterShowRemandedOnly = false;
    let filterShowNonDischargedOnly = false;
    let filterShowTriggerDatesOnly = false;
    let filterShowWarningContactsOnly = false;
    let filterSiteName = "";
    let filterFreeText = "";
    let controlShowScoreLabels = true;
    let controlShowRecentChanges = false;

    // Modal related variables
    let selectedInmate = null;
    let selectedModalType = null;

    // Automatically update filtered inmates list when sort property or filters are changed.
    $: {
        inmates.sort(sortByProperty(sortProperty, reverseSorted));
 
        filteredInmates = inmates.filter(function(inmate) {
            if ($filterShowTargetedInmatesOnly && !inmate.indicatorTargeted) {
                return false; }
            if (filterShowRemandedOnly && !inmate.indicatorRemand) {
                return false; }
            if (filterShowNonDischargedOnly && inmate.dateTemporaryDischarged) {
                return false; }
            if (filterShowTriggerDatesOnly && !(inmate.triggerDateList && inmate.triggerDateList.length > 0)) {
                return false; }
            if (filterShowWarningContactsOnly && !inmate.contactWarning) { 
                return false; }
            if ($config.showSiteName && filterSiteName != "" && !(filterSiteName == inmate.siteName)) {
                return false; }
            if (filterFreeText != "" && !isMatching(getInmateSearchModel(inmate), filterFreeText.trim())) {
                return false; }
            return true;
        });
    }

    $: {
        if (requestProperties && requestProperties.requestDate) {
            searchCompleted = false;
            getDailyReport(requestProperties.requestDate + createRequestParams(requestProperties.requestParams));
        }
    }

    $: keyworkerExists = inmates.filter(i => i.keyworkerName).length > 0;

    async function getDailyReport(requestString) {
        await fetch(`${$vptServer}/establishment/earlyrisk/list/${requestString}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            data.forEach((i,index,arr) => {
                arr[index] = {...i,
                    ...hydrateInmate(i, i.indicatorTrend, $referenceDataEthnicityList, $referenceDataRegimeLevelList, $referenceDataCellStatusList, requestProperties.requestDate, $config.numberOfDaysConsideredNewCircumstance),
                    ...getTabTargetedValues(arr[index].assessmentTabs)
                };
            });
            inmates = data;
        })
        .catch(error => {
            console.error("Error getting early risk list");
            console.error(error);
            return;
        });

        if ($config.showSiteName) {
            sites = [];
            filteredInmates.forEach((inmate) => {
                if (inmate.siteUrn && !(sites.some(e => e.label === inmate.siteName))) {
                    let site = {value: inmate.siteUrn, label: inmate.siteName}
                    sites = [...sites, site];
                }
            });
            sites = sites.sort(sortByProperty("label", false));
        }

        searchCompleted = true;
    }

    function getTabTargetedValues(assessmentTabs) {
        let targetedTabs = assessmentTabs.map(t => {
            return {['targetedOnTab' + t.vptEarlyRiskTabUrn] : t.targetedOnTab ? 1 : 2}
        });
        return Object.assign({}, ...targetedTabs);
    }

    function sort(newSortProperty, reverse) {
        if (newSortProperty === sortProperty) {
            reverseSorted = !reverseSorted;
        }
        else {
            sortProperty = newSortProperty;
            reverseSorted = reverse ? true : false;
        }
    }

    function openModal(inmate, modalType) {
        selectedModalType = modalType;
        if (modalType === ModalTypeEnum.SURVEY) {
            selectedInmate = getInmateSurveyModel(inmate);
        } else if (modalType === ModalTypeEnum.ADD_CONTACT) {
            selectedInmate = { inmateUrn: inmate.inmateUrn, inmateName: inmate.inmateName, unseenDays : inmate.contactTargetedUnseenDays };
        } else if (modalType === ModalTypeEnum.CONTACT) {
            selectedInmate = getInmateContactModel(inmate);
        }
    }

    function closeModal(event) {
        if (event.detail.scheduleType != null) {
            toastProperties = getSurveyToastProperties(event.detail.scheduleType, event.detail.surveyScheduleTotal, selectedInmate.inmateName, selectedInmate.inmateUrn);
        }

        selectedInmate = null;
        selectedModalType = null;
        
        if (event.detail.refresh) {
            if (requestProperties && requestProperties.requestDate) {
                searchCompleted = false;
                getDailyReport(requestProperties.requestDate + createRequestParams(requestProperties.requestParams));
            }
        }
    }

    function countTargetingTriggerTabs(tabs) {
        return tabs.filter(t => {return t.targetingTrigger}).length;
    }
</script>

<div class="container-fluid">
    {#if searchCompleted && inmates.length > 0}
        <div class="row table-filters">
            <div class="col-sm-12">
                <div class="form-inline" role="toolbar" aria-label="Table filters">
                    <div>
                        <FilterButton on:click={() => {filterShowTargetedInmatesOnly.set(!($filterShowTargetedInmatesOnly))}} id="filter-targeted"
                                isActive={$filterShowTargetedInmatesOnly} classes={"mr-3 btn-img"}
                                title="Show only Targeted ({$config.earlyRiskFlagDescription}) {$config.inmateLabelName.toLowerCase()}s">
                            <img src="/assets/filter/target{$filterShowTargetedInmatesOnly ? '' : '-inverse'}.svg" alt={"Show only Targeted"}/>
                        </FilterButton>

                        <FilterButton on:click={() => {filterShowTriggerDatesOnly = !filterShowTriggerDatesOnly}} id="filter-trigger-dates"
                            isActive={filterShowTriggerDatesOnly} icon={"pin-angle-fill"} classes={"mr-1"}
                            title="Show only {$config.inmateLabelName.toLowerCase()}s with recent or upcoming {$config.triggerDatesLabelName}" />
                                            
                        {#if $config.numberOfDaysToShowTemporaryDischarge}
                            <FilterButton on:click={() => {filterShowNonDischargedOnly = !filterShowNonDischargedOnly}} id="filter-non-discharged"
                                isActive={filterShowNonDischargedOnly} icon={"building-fill"} classes={"mr-1"}
                                title="Show only {$config.inmateLabelName.toLowerCase()}s who are currently on the premises" />
                        {/if}

                        <FilterButton on:click={() => {filterShowRemandedOnly = !filterShowRemandedOnly}} id="filter-remand"
                            isActive={filterShowRemandedOnly} icon={"hourglass-split"} classes={"mr-3"}
                            title="Show only {$config.inmateLabelName.toLowerCase()}s on Remand" />

                        {#if $config.contactTypeId}
                            <FilterButton on:click={() => {filterShowWarningContactsOnly = !filterShowWarningContactsOnly}} id="filter-warning-contacts"
                                isActive={filterShowWarningContactsOnly} icon={"sticky"} alertIcon={"exclamation-circle"} classes={"mr-3"}
                                title="Show only {$config.inmateLabelName.toLowerCase()}s where Contact is suggested" />
                        {/if}

                        <FilterButton on:click={()=> {controlShowScoreLabels = !controlShowScoreLabels}} id="control-label"
                            isActive={controlShowScoreLabels} icon={"card-text"} classes={"mr-1"}
                            title={"Show total labels on answers"} />

                        <FilterButton on:click={()=> {controlShowRecentChanges = !controlShowRecentChanges}} id="control-recent-changes"
                            isActive={controlShowRecentChanges} icon={"shuffle"} title={"Highlight recent circumstance changes"} />
                    </div>

                    <div>
                        {#if $config.showSiteName && sites.length > 1}
                            <Dropdown bind:value={filterSiteName} id={"sites"} options={sites} allLabel={"All Sites"} classes="mr-3"/>
                        {/if}

                        <label for="search" class="sr-only">Free Text Search</label>
                        <input type="text" class="form-control" id="search" bind:value={filterFreeText} placeholder="Search..." />
                    </div>
                </div>
            </div>
        </div>
    {/if}

    <div class="row">
        <div class="col-sm-12">
            {#if searchCompleted}
                {#if filteredInmates.length > 0 && !($inmateCount.filtered > $inmateCount.unfiltered)}
                    <table class="table table-sm table-borderless table-hover table-card">
                        <thead>
                            <tr>
                                <TableHead on:click={() => sort("rowOrdering")} propertyName={"rowOrdering"} width={'xs'}
                                        tooltipId={"target-header"} tooltipText={`${$config.inmateLabelName} is on the Targeted list(s)`} 
                                        {sortProperty} {reverseSorted} invertArrow={true}>
                                    <img id="target-header" src="/assets/header/target.svg" alt="{$config.inmateLabelName} is on the Targeted list(s)" />
                                </TableHead>

                                <TableHead on:click={() => sort("inmateName")} propertyName={"inmateName"} title={$config.inmateLabelName}
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("triggerDateSort", true)} propertyName={"triggerDateSort"} icon={"pin-angle-fill"}
                                    tooltipText={`Recent / Upcoming ${$config.triggerDatesLabelName}`} tooltipId={"trigger-date-header"} 
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("hoursSinceArrival")} propertyName={"hoursSinceArrival"} title={"Arrived"}
                                    {sortProperty} {reverseSorted} />

                                {#if (!isDateInPast($reportDate))}
                                    <TableHead on:click={() => sort("hoursSinceArrival")} propertyName={"hoursSinceArrival"} title={"Remaining"}
                                        tooltipId={"remaining-header"} tooltipText="Hours remaining in the Early Risk period"
                                        {sortProperty} {reverseSorted} invertArrow={true} width={'lg'} />
                                {/if}

                                <TableHead on:click={() => sort("cellStatusSort")} propertyName={"cellStatusSort"} icon={"file-person"}
                                    tooltipText="Cell Status" tooltipId={"cell-status-header"}
                                    {sortProperty} {reverseSorted} invertArrow={true} />

                                <TableHead on:click={() => sort("wingCellSort")} propertyName={"wingCellSort"} title={"Cell"}
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("daysSinceTemporaryDischarge", true)} propertyName={"daysSinceTemporaryDischarge"} 
                                    icon={"building-fill"} tooltipText={"Temporary Discharge"} tooltipId={"temp-discharge-header"} 
                                    {sortProperty} {reverseSorted} />

                                {#each referenceDataEarlyRiskTabList as earlyRiskTab}
                                    {#if earlyRiskTab.checklistUrn == $config.earlyRiskChecklistUrn && earlyRiskTab.targetingTrigger}
                                        <TableHead on:click={() => sort(`targetedOnTab${earlyRiskTab.vptEarlyRiskTabUrn}`)} propertyName={`targetedOnTab${earlyRiskTab.vptEarlyRiskTabUrn}`}
                                            title={earlyRiskTab.name} {sortProperty} {reverseSorted} invertArrow={true} center={true} />
                                    {/if}
                                {/each}

                                <TableHead on:click={() => sort("rowOrdering")} propertyName={"rowOrdering"} title={"Answers"}
                                    tooltipId={"answers-header"} tooltipText={`Responses to the ${$config.earlyRiskAssessmentDescription}`}
                                    {sortProperty} {reverseSorted} invertArrow={true} center={true} width={'xl'} />

                                <TableHead on:click={() => sort("regimeLevelDescription")} propertyName={"regimeLevelDescription"} 
                                    title={"Regime"} {sortProperty} {reverseSorted} classes={"hidden-col-lv-3 table-pad-left"} />

                                <TableHead on:click={() => sort("indicatorRemand", true)} propertyName={"indicatorRemand"} icon={"hourglass-split"}
                                    tooltipText={`${$config.inmateLabelName} is on remand`} tooltipId={"remand-header"} 
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("status")} propertyName={"status"} 
                                    title={"Status"} {sortProperty} {reverseSorted} classes="hidden-col-lv-2" />

                                {#if keyworkerExists}
                                    <TableHead on:click={() => sort("keyworkerName")} propertyName={"keyworkerName"} 
                                        title={$config.keyworkerLabelName} {sortProperty} {reverseSorted} />
                                {/if}

                                {#if $config.showSiteName && sites.length > 1}
                                    <TableHead on:click={() => sort("siteName")} propertyName={"siteName"}
                                        title={"Site"} {sortProperty} {reverseSorted} classes="hidden-col-lv-1" />
                                {/if}

                                {#if $config.contactTypeId && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                    <TableHead on:click={() => sort("contactSort", true)} propertyName={"contactSort"}
                                        icon="sticky" tooltipText="Contact" tooltipId={"contact-header"}
                                        {sortProperty} {reverseSorted} action={true} />
                                {/if}

                                {#if $config.surveyUrn && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                    <TableHead on:click={() => sort("surveyScheduleNextSurveyDate")} propertyName={"surveyScheduleNextSurveyDate"}
                                        icon="clipboard-data" tooltipText="{$config.surveyLabelName} Survey Schedule" tooltipId={"survey-header"}
                                        {sortProperty} {reverseSorted} invertArrow={true} action={true} />
                                {/if}
                            </tr>
                        </thead>

                        <tbody>
                            {#each filteredInmates as inmate (inmate.inmateUrn)}
                                <tr on:click={() => {navigate(`/history/${inmate.inmateUrn}`)}}>
                                    
                                    <!-- Targeted Indicator -->                                
                                    <TargetedData inmateUrn={inmate.inmateUrn} targeted={inmate.indicatorTargeted} 
                                        targetedTotal={inmate.targetedTotal} inmateLabel={$config.inmateLabelName} />

                                    <!-- Inmate -->
                                    <InmateData inmateUrn={inmate.inmateUrn} name={inmate.inmateName} 
                                        tooltipText={getInmateDetailsTooltip(inmate)} tooltipOptions={getTooltipDisplayOptions({ position: "right" })} />
                                
                                    <!-- Trigger Date Indicator -->
                                    <TableData display={inmate.triggerDateList && inmate.triggerDateList.length > 0} icon={"pin-angle-fill"}
                                        aimTooltip={true} tooltipOptions={getTooltipDisplayOptions({ align: "left", maxWidth: 500 })}
                                        tooltipText={getTriggerDatesTooltip(inmate.triggerDateList)}
                                        classes={isDateInTriggerDates(requestProperties.requestDate, inmate.triggerDateList) ? "text-danger" : ""} />
                                    
                                    <!-- Arrived -->
                                    <TableData text={getArrivalDateAndTimeText($reportDate, inmate.dateArrived, inmate.hoursSinceArrival)} 
                                        aimTooltip={true} tooltipOptions={getTooltipDisplayOptions()} tooltipText={getArrivalDateAndTimeTooltip(inmate)} />

                                    <!-- Remaining -->
                                    {#if (!isDateInPast($reportDate))}
                                        <TableData alignMiddle={true} classes="table-pad-right" tooltipId={`time-${inmate.inmateUrn}`} 
                                                tooltipText={`${$config.earlyRiskNumberOfHoursPeriod - inmate.hoursSinceArrival} of ${$config.earlyRiskNumberOfHoursPeriod} hours remaining in the Early Risk period`}>
                                            <div class="progress progress-thin" id="time-{inmate.inmateUrn}">
                                                <div class="progress-bar progress-bar-primary" role="progressbar" 
                                                    style="width: {100 -((inmate.hoursSinceArrival / $config.earlyRiskNumberOfHoursPeriod) * 100)}%" 
                                                    aria-valuenow="{100 -((inmate.hoursSinceArrival / $config.earlyRiskNumberOfHoursPeriod) * 100)}" 
                                                    aria-valuemin="0" aria-valuemax="100">
                                                </div>
                                            </div>
                                        </TableData>
                                    {/if}

                                    <!-- Cell Status -->
                                    {#if $config.showCellStatusIcon}
                                        <TableData display={inmate.vptCellStatusUrn > 0 && inmate.cellStatusIcon} center={true} 
                                                tooltipId={`cell-status-data-${inmate.inmateUrn}`} tooltipText={inmate.cellStatusDescription}>
                                            <img id="cell-status-data-{inmate.inmateUrn}" src="/assets/{inmate.cellStatusIcon}.svg" alt="{inmate.cellStatusDescription}" />
                                        </TableData>
                                    {/if}

                                    <!-- Cell Location -->
                                    <LocationData inmateUrn={inmate.inmateUrn} unit={inmate.unit} wing={inmate.wing} cell={inmate.cell} 
                                        cellStartDate={inmate.cellStartDate} cellFinishDate={inmate.cellFinishDate} 
                                        highlight={controlShowRecentChanges && inmate.isRecentCellChange} />

                                    <!-- Temporary Discharge Indicator -->
                                    <TableData display={inmate.daysSinceTemporaryDischarge > 0} icon={"building-fill-x"}
                                        aimTooltip={true} tooltipOptions={getTooltipDisplayOptions({ maxWidth: 500 })}
                                        tooltipText={getTemporaryDischargeTooltip(inmate.daysSinceTemporaryDischarge, inmate.dischargedTo)} />

                                    <!-- Display the outcome of each Early Risk section unless the RSRA has not been started -->
                                    {#if inmate.indicatorCompleted == false && inmate.assessmentUrn == null}
                                        <td class="table-text-well" colspan={countTargetingTriggerTabs(inmate.assessmentTabs)}>
                                            <span class="table-text-center">
                                                <i class="bi bi-exclamation-lg text-danger" /> The {$config.earlyRiskAssessmentLabelName} has not been started
                                            </span>
                                        </td>
                                    {:else}
                                        {#each inmate.assessmentTabs as assessmentTab}
                                            {#if assessmentTab.targetingTrigger}
                                                 {#if assessmentTab.targetedOnTab}
                                                    <TableData icon={"exclamation-triangle-fill"} iconSize={'lg'} center={true} classes="text-danger"
                                                        tooltipText={`${$config.earlyRiskAssessmentLabelName} indicated ${getReferenceDataItemFromList(assessmentTab.vptEarlyRiskTabUrn, referenceDataEarlyRiskTabList, 'name', 'vptEarlyRiskTabUrn')} as a possible risk factor`}
                                                        tooltipId={`tab-${inmate.inmateUrn}-${assessmentTab.vptEarlyRiskTabUrn}`} />
                                                {:else}
                                                    <TableData display={inmate.indicatorCompleted == false} text={"Incomplete"} center={true} classes="table-text-muted"
                                                        tooltipText={inmate.nonCompletionReason ? inmate.nonCompletionReason : null}
                                                        tooltipId={`incomplete-${inmate.inmateUrn}-${assessmentTab.vptEarlyRiskTabUrn}`} />
                                                {/if}
                                            {/if}
                                        {/each}
                                    {/if}

                                    <!-- Answers -->
                                    <TableData>
                                        <div class="progress">
                                            {#each inmate.assessmentAnswerGroups as assessmentAnswer}
                                                <div class="progress-bar {getEarlyRiskColourClass(assessmentAnswer.ragstatus)}" id="answers-{inmate.inmateUrn}-{assessmentAnswer.ragstatus}" role="progressbar" style="width: {assessmentAnswer.percentage}%" aria-valuenow="{assessmentAnswer.percentage}" aria-valuemin="0" aria-valuemax="100">
                                                    {controlShowScoreLabels ? assessmentAnswer.total : ''}
                                                    <Tooltip target="answers-{inmate.inmateUrn}-{assessmentAnswer.ragstatus}" placement="bottom">
                                                        {getEarlyRiskColourName(assessmentAnswer.ragstatus)} ({assessmentAnswer.percentage}%)
                                                    </Tooltip>
                                                </div>
                                            {/each}
                                        </div>
                                    </TableData>

                                    <!-- Regime -->
                                    <TableData display={inmate.regimeLevelDescription} text={inmate.regimeLevelDescription} tooltipId={`regime-data-${inmate.inmateUrn}`}
                                        tooltipText={getCircumstanceDateTooltip(inmate.regimeStartDate, inmate.regimeFinishDate)} 
                                        highlight={controlShowRecentChanges && inmate.isRecentRegimeChange} classes="hidden-col-lv-3 table-pad-left" />

                                    <!-- Remand Indicator -->
                                    <TableData display={inmate.indicatorRemand} icon={"hourglass-split"} />

                                    <!-- Status -->
                                    <TableData display={inmate.status} text={inmate.status} tooltipId={`status-data-${inmate.inmateUrn}`}
                                        tooltipText={getCircumstanceDateTooltip(inmate.statusStartDate, inmate.statusFinishDate)}
                                        highlight={controlShowRecentChanges && inmate.isRecentStatusChange} classes={"hidden-col-lv-2"} />

                                    <!-- Keyworker -->
                                    {#if keyworkerExists}
                                        <TableData display={inmate.keyworkerName} text={inmate.keyworkerName} tooltipId={`keyworker-data-${inmate.inmateUrn}`}
                                            tooltipText={getCircumstanceDateTooltip(inmate.keyworkerStartDate, inmate.keyworkerFinishDate)} 
                                            highlight={controlShowRecentChanges && inmate.isRecentKeyworkerChange} />
                                    {/if}

                                    <!-- Site -->
                                    {#if $config.showSiteName && sites.length > 1}
                                        <TableData display={inmate.siteName} text={inmate.siteName} classes="hidden-col-lv-1" />
                                    {/if}

                                    <!-- Contact -->
                                    <TableAction display={$config.contactTypeId && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                        icon={"sticky"} displayFilled={inmate.contactId} alertIcon={inmate.contactOverdue ? "clock" : "exclamation-circle"} displayAlert={inmate.contactWarning} 
                                        tooltipText={getContactWarningTooltip(inmate.contactId, inmate.contactTargetedUnseenDays, inmate.contactActionOverdueDays, inmate.contactIndicatorTargetedTotalIncreased, inmate.contactIndicatorIsolationIncreased)}
                                        tooltipOptions={getTooltipDisplayOptions({ position: "left" })}
                                        on:click={() => {openModal(inmate, inmate.contactId ? ModalTypeEnum.CONTACT : ModalTypeEnum.ADD_CONTACT)}} />

                                    <!-- Survey Schedule -->
                                    <TableAction display={$config.surveyUrn && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                        icon={"clipboard-data"} displayFilled={inmate.surveyScheduleUrn} alertIcon={"clock"} displayAlert={inmate.surveyOverdue}
                                        tooltipText={getSurveyScheduleTooltip(inmate)} tooltipOptions={getTooltipDisplayOptions({ position: "left" })}
                                        on:click={() => {openModal(inmate, ModalTypeEnum.SURVEY)}} />
                                </tr>
                            {/each}
                        </tbody>
                    </table>

                    {#if selectedInmate}
                        {#if selectedModalType === ModalTypeEnum.SURVEY}
                            <SurveyModal {...selectedInmate} on:close={closeModal} />
                        {:else if selectedModalType === ModalTypeEnum.ADD_CONTACT}
                            <AddContactModal {...selectedInmate} on:close={closeModal} />
                        {:else if selectedModalType === ModalTypeEnum.CONTACT}
                            <ContactModal {...selectedInmate} on:close={closeModal} />
                        {/if}
                    {/if}
                {:else}
                    {#if ($inmateCount.filtered > $inmateCount.unfiltered) || ($inmateCount.unfiltered == 0 && isToday(getIsoDateStringAsDate(requestProperties.requestDate)))}
                        <div class="alert alert-warning" role="alert">
                            Data is being regenerated. Please wait a few moments and
                            <button type="button" class="btn btn-link refresh" on:click={() => dispatch("refresh")}>
                                Refresh
                            </button>
                        </div>
                    {:else if $inmateCount.unfiltered > 0}
                        <div class="alert alert-warning" role="alert">No {$config.inmateLabelName.toLowerCase()}s are currently being displayed. Please check the selected filters.</div>
                    {:else}
                        <div class="alert alert-danger" role="alert">No {$config.earlyRiskAssessmentLabelName} records were found for the selected date. Please try selecting a different date.</div>
                    {/if}
                {/if}
            {:else}
                <div class="spinner-container">
                    <img id="aim-icon" src="/assets/logo-aim-target.png" alt="AIM Logo" />
                    <img id="aim-spinner" src="/assets/logo-radar-spinner.png" alt="AIM Logo" />
                </div>
            {/if}
        </div>
    </div>
</div>

<AimToast {...toastProperties}></AimToast>